import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Browser voice layer for the AI Assistant — always ALONGSIDE text, never
 * voice-only. Both hooks feature-detect and no-op (supported: false) where the
 * APIs are missing (e.g. Firefox mic input), so callers just hide the buttons.
 */

/**
 * Speech-to-text via (webkit)SpeechRecognition. The final transcript is handed
 * to `onFinal` — the composer APPENDS it for the member to review and send;
 * dictation never auto-sends.
 */
export function useSpeechInput(onFinal: (text: string) => void): {
  supported: boolean;
  listening: boolean;
  start: () => void;
  stop: () => void;
} {
  const Ctor =
    typeof window !== 'undefined'
      ? (window.SpeechRecognition ?? window.webkitSpeechRecognition)
      : undefined;
  const supported = Boolean(Ctor);
  const [listening, setListening] = useState(false);
  const recRef = useRef<SpeechRecognitionLike | null>(null);
  const onFinalRef = useRef(onFinal);
  onFinalRef.current = onFinal;

  useEffect(() => {
    return () => {
      recRef.current?.abort();
      recRef.current = null;
    };
  }, []);

  const stop = useCallback(() => {
    recRef.current?.stop();
  }, []);

  const start = useCallback(() => {
    if (!Ctor || recRef.current) return;
    const rec = new Ctor();
    rec.continuous = false;
    rec.interimResults = false;
    rec.lang = navigator.language || 'en-PH';
    rec.onresult = (event) => {
      const parts: string[] = [];
      for (let i = 0; i < event.results.length; i += 1) {
        const alt = event.results[i]?.[0];
        if (event.results[i]?.isFinal && alt?.transcript) parts.push(alt.transcript);
      }
      const text = parts.join(' ').trim();
      if (text) onFinalRef.current(text);
    };
    rec.onend = () => {
      recRef.current = null;
      setListening(false);
    };
    rec.onerror = () => {
      recRef.current = null;
      setListening(false);
    };
    recRef.current = rec;
    setListening(true);
    rec.start();
  }, [Ctor]);

  return { supported, listening, start, stop };
}

/** Strip markdown down to speakable plain text. */
export function markdownToSpeech(md: string): string {
  return md
    .replace(/```[\s\S]*?```/g, ' ') // code blocks
    .replace(/`([^`]*)`/g, '$1')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ') // images
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1') // links → text
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/[*_~>#|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Text-to-speech via speechSynthesis; cancels on unmount and re-speak. */
export function useSpeechOutput(): {
  supported: boolean;
  speakingId: string | null;
  speak: (id: string, text: string, voiceHint?: string) => void;
  cancel: () => void;
} {
  const supported = typeof window !== 'undefined' && 'speechSynthesis' in window;
  const [speakingId, setSpeakingId] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (supported) window.speechSynthesis.cancel();
    };
  }, [supported]);

  const cancel = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.cancel();
    setSpeakingId(null);
  }, [supported]);

  const speak = useCallback(
    (id: string, text: string, voiceHint?: string) => {
      if (!supported) return;
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(markdownToSpeech(text));
      if (voiceHint) {
        // Best-effort: match a voice by name/lang substring; silently keep the
        // default voice when nothing matches.
        const voice = window.speechSynthesis
          .getVoices()
          .find(
            (v) =>
              v.name.toLowerCase().includes(voiceHint.toLowerCase()) ||
              v.lang.toLowerCase().startsWith(voiceHint.toLowerCase()),
          );
        if (voice) utterance.voice = voice;
      }
      utterance.onend = () => setSpeakingId((cur) => (cur === id ? null : cur));
      utterance.onerror = () => setSpeakingId((cur) => (cur === id ? null : cur));
      setSpeakingId(id);
      window.speechSynthesis.speak(utterance);
    },
    [supported],
  );

  return { supported, speakingId, speak, cancel };
}
